<template>
  <a-alert
    v-if="showBanner"
    :class="bannerClass"
    :type="alertType"
    banner
    square
    :show-icon="false"
  >
    <template v-if="useTrialBanner">
      <template slot="description">
        <template v-if="daysLeft > 7">
          <span class="emoji">🎉</span>
          You have <b>{{ daysLeft }} days</b> left in your OpsLevel free trial.
          Want to make it official?
        </template>

        <template v-else-if="daysLeft >= 1">
          <span class="emoji">⏰</span>
          Time's running out on your free trial, you have just
          <b>{{ daysLeft }} {{ pluralize("day", daysLeft) }}</b> left.
        </template>

        <template v-else>
          <span class="emoji">🚫</span>
          Looks like your trial has expired.
        </template>
        <a-button
          v-if="showBookADemo"
          type="ghost"
          class="book-demo-button"
          :style="{ marginLeft: '8px' }"
          @click="bookADemo"
        >
          Book a Demo
        </a-button>
      </template>
    </template>

    <template v-else-if="creditsExhausted">
      <template slot="description">
        <span class="emoji">🚫</span>
        Your trial account has run out of credits.
        <a :href="upgradeAccountLink">Upgrade your account</a> to generate code
        changes using {{ $t("brandName") }}.
      </template>
    </template>

    <template v-else-if="expiredTrial">
      <template slot="description">
        <span class="emoji">🚫</span>
        Your trial period has ended.
        <a :href="upgradeAccountLink">Upgrade your account</a> to continue
        generating code changes using {{ $t("brandName") }}.
      </template>
    </template>

    <template v-else-if="isPilot">
      <template slot="description">
        <template v-if="daysLeft > 7">
          <span class="emoji">🎉</span>
          You have <b>{{ daysLeft }} days</b> left in your OpsLevel Pilot. Let
          us know if you have any questions:
          <a :href="mailToSupport">{{ supportAddress }}</a
          >.
        </template>

        <template v-else-if="daysLeft >= 1">
          <span class="emoji">⏰</span>
          Time's running out. You have
          <b>{{ daysLeft }} {{ pluralize("day", daysLeft) }}</b> left in your
          pilot. Let us know if you have any questions:
          <a :href="mailToSupport">{{ supportAddress }}</a
          >.
        </template>

        <template v-else>
          <span class="emoji">🚫</span>
          Your pilot has expired. Reach out to
          <a :href="mailToSupport">{{ supportAddress }}</a> to keep building.
        </template>
      </template>
    </template>
  </a-alert>
</template>

<script>
import pluralize from "pluralize";
import { featureFlags } from "@/mixins/featureFlags.js";
import { systemToggles } from "@/mixins/systemToggles.js";
import { mapActions } from "vuex";
import { PaymentPlans } from "@/shared/payment_plans_helper.js";

const BOOK_A_DEMO_URL =
  "https://opslevel.na.chilipiper.com/book/custom-demo-request";

const PlanExpiryWarningThreshold = 21;

export default {
  mixins: [featureFlags, systemToggles],

  inject: {
    brand: { default: "opslevel" },
    account: { default: () => {} },
    routes: { default: () => ({}) },
  },

  props: {
    daysLeft: {
      type: Number,
      default: null,
    },
    planName: {
      type: String,
      required: true,
    },
    canExpire: {
      type: Boolean,
      default: true,
    },
    creditsRemaining: {
      type: Number,
      default: null,
    },
  },

  computed: {
    showBanner() {
      if (!this.planName) {
        return false;
      }

      if (this.isTrial) {
        return this.creditsExhausted || this.expiredTrial;
      }

      return (
        this.canExpire &&
        this.daysLeft >= 0 &&
        this.daysLeft <= PlanExpiryWarningThreshold
      );
    },
    supportAddress() {
      return this.account?.supportEmail ?? "";
    },
    mailToSupport() {
      return `mailto:${this.supportAddress}?subject=Account%20Expired`;
    },
    upgradeAccountLink() {
      if (this.brand === "tidra") {
        return this.routes.account_plan_path();
      }

      return `mailto:${this.supportAddress}?subject=Upgrade%20Plan`;
    },
    creditsExhausted() {
      return (
        this.isTrial &&
        this.creditsRemaining !== null &&
        this.creditsRemaining <= 0
      );
    },
    alertType() {
      if (this.creditsExhausted || this.expiredTrial) {
        return "warning";
      }

      if (this.useTrialBanner) {
        return this.daysLeft > 5 ? "success" : "error";
      }

      if (this.daysLeft > 7) {
        return "success";
      } else if (this.daysLeft >= 1) {
        return "warning";
      } else {
        return "error";
      }
    },
    bannerClass() {
      return this.useTrialBanner ? "trial-banner" : "";
    },
    showBookADemo() {
      return this.brand === "opslevel";
    },
    useTrialBanner() {
      return this.planName === "Free Trial";
    },
    isPilot() {
      return (
        this.planName === PaymentPlans.pilot ||
        this.planName === PaymentPlans.paidPilot
      );
    },
    isTrial() {
      return this.planName === PaymentPlans.trial;
    },
    expiredTrial() {
      return this.isTrial && this.canExpire && this.daysLeft <= 0;
    },
  },

  methods: {
    ...mapActions({
      trackUserClick: "tracking/trackUserClick",
    }),
    pluralize,
    bookADemo() {
      window.open(BOOK_A_DEMO_URL, "_blank");
    },
  },
};
</script>

<style scoped lang="scss">
.emoji {
  margin-right: 6px;
}

.trial-banner.ant-alert-success {
  color: white;
  background: linear-gradient(90deg, #13c2c2 30%, #1890ff);
}
.trial-banner.ant-alert-success a {
  color: white;
  text-decoration: underline;
}
.trial-banner.ant-alert-error {
  background: linear-gradient(90deg, #ffa39e, #f5222d);
}

.book-demo-button {
  opacity: 0.9;

  &:hover,
  &:focus {
    color: white;
    border-color: white;
    opacity: 1;
  }
}
</style>
